import { useEffect, useMemo, useState } from "react";
import { api, setAdminToken } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { Loader2, Star, Users, Send, Trash2, X, Mail, CheckCircle2, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { key: "loyalty", label: "Loyalty Program", Icon: Star, color: "spark-yellow" },
  { key: "refer_a_mate", label: "Refer a Mate", Icon: Users, color: "spark-teal" },
];

const featureMeta = (key) => FEATURES.find((f) => f.key === key) || { key, label: key, Icon: Mail };

const AdminInterest = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ signups: [], counts: {}, notified_counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all' | 'loyalty' | 'refer_a_mate'
  const [composerFor, setComposerFor] = useState(null); // feature key

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/interest");
      setData(res.data || { signups: [], counts: {}, notified_counts: {} });
    } catch (err) {
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
      } else {
        toast.error("Couldn't load the waitlist");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this signup? They won't get the launch email.")) return;
    try {
      await api.delete(`/admin/interest/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return data.signups;
    return data.signups.filter((s) => s.feature === filter);
  }, [data.signups, filter]);

  return (
    <AdminLayout eyebrow="Growth" title="Interest waitlists">
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="size-6 animate-spin text-spark-yellow" /></div>
      ) : (
        <div className="space-y-6" data-testid="admin-interest-page">
          {/* Feature summary cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            {FEATURES.map((f) => {
              const total = data.counts?.[f.key] || 0;
              const notified = data.notified_counts?.[f.key] || 0;
              const pending = total - notified;
              return (
                <div key={f.key} className="rounded-xl border border-white/10 bg-white/[0.04] p-5" data-testid={`interest-card-${f.key}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <f.Icon className={`size-4 text-${f.color}`} />
                        <p className="label-mono text-spark-yellow">{f.label}</p>
                      </div>
                      <p className="font-display text-4xl font-bold text-white leading-none mt-2">{total}</p>
                      <p className="text-xs text-slate-400 mt-1.5">
                        <strong className="text-white">{pending}</strong> pending notification
                        {notified > 0 && <> · <strong className="text-white">{notified}</strong> already emailed</>}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setComposerFor(f.key)}
                      disabled={total === 0}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-300 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                      data-testid={`interest-blast-${f.key}`}
                    >
                      <Send className="size-3.5" /> Blast
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1.5 border-b border-white/10">
            {[{ key: "all", label: `All (${data.signups.length})` }, ...FEATURES.map((f) => ({ key: f.key, label: `${f.label} (${data.counts?.[f.key] || 0})` }))].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-3 py-2 text-xs font-display uppercase tracking-wider font-bold border-b-2 -mb-px transition ${filter === tab.key ? "text-white border-spark-yellow" : "text-slate-500 border-transparent hover:text-slate-200"}`}
                data-testid={`interest-filter-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Signups list */}
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-10 text-center">
              <p className="text-slate-400 text-sm">No signups on this waitlist yet.</p>
            </div>
          ) : (
            <ul className="rounded-xl border border-white/10 bg-white/[0.04] overflow-hidden divide-y divide-white/5" data-testid="interest-signups-list">
              {filtered.map((s) => {
                const meta = featureMeta(s.feature);
                return (
                  <li key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid={`interest-signup-${s.id}`}>
                    <div className="size-9 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                      <meta.Icon className="size-4 text-slate-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-white font-semibold truncate">{s.name || "(no name)"}</p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white/10 text-slate-300 uppercase tracking-wider font-bold">{meta.label}</span>
                        {s.notified_at && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-300 uppercase tracking-wider font-bold"><CheckCircle2 className="size-3" /> Notified</span>}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{s.email} · joined {new Date(s.created_at).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}</p>
                    </div>
                    <button onClick={() => remove(s.id)} className="p-1.5 rounded text-slate-500 hover:text-red-400" title="Remove" data-testid={`interest-remove-${s.id}`}>
                      <Trash2 className="size-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {composerFor && (
        <BlastComposer
          feature={composerFor}
          total={data.counts?.[composerFor] || 0}
          pending={(data.counts?.[composerFor] || 0) - (data.notified_counts?.[composerFor] || 0)}
          alreadyNotified={data.notified_counts?.[composerFor] || 0}
          onClose={() => setComposerFor(null)}
          onSent={() => { setComposerFor(null); load(); }}
        />
      )}
    </AdminLayout>
  );
};

const BlastComposer = ({ feature, total, pending, alreadyNotified, onClose, onSent }) => {
  const meta = featureMeta(feature);
  const [subject, setSubject] = useState(feature === "loyalty" ? "The Spark Loyalty Program is live" : "The Refer-a-Mate program is live");
  const [headline, setHeadline] = useState(feature === "loyalty" ? "Your loyalty rewards are here" : "Bring a mate — both of you win");
  const [bodyHtml, setBodyHtml] = useState(
    feature === "loyalty"
      ? "<p>Hey {{first_name}},</p><p>You put your hand up when we teased the Spark Loyalty Program — well, it's live from today. You've been auto-enrolled based on your service history, and there are perks waiting for you.</p><p>Head to your tracker to see your current tier and what you've unlocked.</p>"
      : "<p>Hey {{first_name}},</p><p>You signed up when we teased Refer a Mate — it's live. Share your unique referral code and both you and your mate score credit toward your next service.</p><p>Grab your code from your tracker any time.</p>"
  );
  const [ctaText, setCtaText] = useState("Open my tracker");
  const [ctaUrl, setCtaUrl] = useState(`${window.location.origin}/track`);
  const [includeNotified, setIncludeNotified] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(null); // 'test' | 'blast'

  const audience = includeNotified ? total : pending;

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter a test email first"); return; }
    setSending("test");
    try {
      await api.post("/admin/interest/blast", {
        feature, subject, headline, body_html: bodyHtml, cta_text: ctaText, cta_url: ctaUrl,
        test_only_to: testEmail.trim(),
      });
      toast.success(`Test sent to ${testEmail.trim()}. Check the inbox before you go live.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally { setSending(null); }
  };

  const sendBlast = async () => {
    if (audience === 0) { toast.error("Nobody left to email in this segment"); return; }
    if (!window.confirm(
      `You're about to email ${audience} customer${audience === 1 ? "" : "s"} for the ${meta.label} launch.\n\n` +
      `Resend is LIVE — this can't be un-sent. Continue?`
    )) return;
    setSending("blast");
    try {
      const res = await api.post("/admin/interest/blast", {
        feature, subject, headline, body_html: bodyHtml, cta_text: ctaText, cta_url: ctaUrl,
        include_previously_notified: includeNotified,
      });
      const sent = res.data?.sent ?? 0;
      const failed = res.data?.failed_count ?? 0;
      if (failed > 0) toast.warning(`Sent to ${sent}. ${failed} failed — check backend logs.`);
      else toast.success(`Sent to ${sent} customer${sent === 1 ? "" : "s"}.`);
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blast failed");
    } finally { setSending(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" role="dialog" aria-modal="true" data-testid="blast-composer-modal">
      <div className="w-full max-w-2xl my-8 rounded-xl border border-white/10 bg-spark-ink shadow-2xl overflow-hidden">
        <div className="bg-spark-teal-deep px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <p className="label-mono text-spark-yellow flex items-center gap-1.5"><meta.Icon className="size-3" /> {meta.label} · Launch blast</p>
            <h2 className="font-display uppercase tracking-tight text-lg font-bold text-white mt-1">Compose the launch email</h2>
            <p className="text-xs text-slate-300 mt-1">
              Emailing <strong className="text-white">{audience}</strong> of {total} · {alreadyNotified > 0 && <>{alreadyNotified} already notified · </>}
              Placeholders <code className="bg-white/10 px-1 rounded text-spark-yellow">{"{{first_name}}"}</code> and <code className="bg-white/10 px-1 rounded text-spark-yellow">{"{{name}}"}</code> are replaced per recipient.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white p-1" data-testid="blast-close"><X className="size-5" /></button>
        </div>
        <div className="p-6 space-y-4 text-slate-100">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">Subject line</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="blast-subject" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">Headline (in the email)</label>
            <input value={headline} onChange={(e) => setHeadline(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="blast-headline" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">Message · HTML allowed</label>
            <textarea rows={8} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono resize-none" data-testid="blast-body" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">CTA button label (optional)</label>
              <input value={ctaText} onChange={(e) => setCtaText(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="blast-cta-text" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">CTA link URL</label>
              <input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="blast-cta-url" />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-300 mt-1">
            <input type="checkbox" checked={includeNotified} onChange={(e) => setIncludeNotified(e.target.checked)} className="size-4 accent-spark-teal" data-testid="blast-include-notified" />
            Also re-send to <strong>{alreadyNotified}</strong> customer{alreadyNotified === 1 ? "" : "s"} previously notified
          </label>

          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 flex gap-2 items-start">
            <AlertTriangle className="size-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-100 leading-relaxed">
              <strong>Resend is LIVE.</strong> Send a test copy to yourself first to double-check the styling before the real blast — a bad email to a real waitlist is very hard to recover from.
            </div>
          </div>

          <div className="pt-3 border-t border-white/10">
            <label className="text-[10px] font-bold uppercase tracking-wider text-spark-yellow mb-1.5 inline-block">Test send</label>
            <div className="flex gap-2 flex-wrap">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 min-w-[220px] bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="blast-test-email"
              />
              <button type="button" onClick={sendTest} disabled={sending === "test" || !testEmail.trim()} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-white/10 border border-white/20 hover:bg-white/15 text-xs uppercase tracking-wider font-bold text-slate-100 disabled:opacity-50" data-testid="blast-send-test">
                {sending === "test" ? <Loader2 className="size-3.5 animate-spin" /> : <Eye className="size-3.5" />} Send test
              </button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-slate-400">Sending to <strong className="text-white">{audience}</strong> customer{audience === 1 ? "" : "s"}</p>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={sending} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-slate-300 hover:bg-white/5" data-testid="blast-cancel">Cancel</button>
            <button onClick={sendBlast} disabled={sending || audience === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-300 disabled:opacity-60" data-testid="blast-send">
              {sending === "blast" ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />} Send blast to {audience}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInterest;
